import React from "react";

type Variant = "light" | "dark";
type Accent = "kana" | "luna" | "stirred" | "dailysign" | "veil";

const accentMap: Record<Accent, { primary: string; soft: string; border: string; ink: string }> = {
  kana: {
    primary: "var(--kana)",
    soft: "rgba(59, 120, 184, 0.1)",
    border: "rgba(59, 120, 184, 0.2)",
    ink: "var(--ink)",
  },
  luna: {
    primary: "var(--lav)",
    soft: "rgba(139, 111, 227, 0.12)",
    border: "rgba(243, 210, 122, 0.18)",
    ink: "#fff",
  },
  stirred: {
    primary: "#EAC568",
    soft: "rgba(234, 197, 104, 0.12)",
    border: "rgba(234, 197, 104, 0.24)",
    ink: "#fff",
  },
  dailysign: {
    primary: "#7C67F2",
    soft: "rgba(124, 103, 242, 0.1)",
    border: "rgba(124, 103, 242, 0.22)",
    ink: "var(--ink)",
  },
  veil: {
    primary: "#8E7AA8",
    soft: "rgba(184, 172, 207, 0.18)",
    border: "rgba(184, 172, 207, 0.4)",
    ink: "var(--ink)",
  },
};

export const createPageStyles = (
  variant: Variant,
  accent: Accent
): Record<string, React.CSSProperties> => {
  const isDark = variant === "dark";
  const colors = accentMap[accent];

  return {
    page: {
      minHeight: "100vh",
      background: isDark ? "var(--bg-deep)" : "var(--bg)",
      color: isDark ? "#F8F5EE" : "var(--ink)",
      padding: "32px 20px 72px",
    },
    shell: {
      width: "100%",
      maxWidth: 1120,
      margin: "0 auto",
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 16,
      flexWrap: "wrap",
      marginBottom: 26,
    },
    homeLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      color: isDark ? "rgba(255,255,255,0.8)" : "var(--ink-2)",
      textDecoration: "none",
      fontSize: 14,
      fontWeight: 600,
    },
    crumb: {
      display: "inline-flex",
      alignItems: "center",
      padding: "7px 12px",
      borderRadius: 999,
      background: isDark ? "rgba(255,255,255,0.06)" : colors.soft,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : colors.border}`,
      color: isDark ? "#F3D27A" : colors.primary,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    topRowMeta: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    localeSwitch: {
      display: "inline-flex",
      gap: 6,
      padding: 4,
      borderRadius: 999,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(28,24,56,0.04)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
    },
    localeButton: {
      minWidth: 42,
      padding: "8px 10px",
      borderRadius: 999,
      border: "none",
      background: "transparent",
      color: isDark ? "rgba(248,245,238,0.68)" : "var(--ink-3)",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
      appearance: "none",
      fontFamily: "inherit",
    },
    activeLocaleButton: {
      background: isDark ? "#F3D27A" : "var(--ink)",
      color: isDark ? "#1C1838" : "#fff",
      boxShadow: isDark
        ? "0 8px 20px rgba(0,0,0,0.22)"
        : "0 8px 20px rgba(28,24,56,0.12)",
    },
    hero: {
      padding: "28px 0 28px",
      borderTop: `1.5px solid ${isDark ? "rgba(255,255,255,0.35)" : "var(--ink)"}`,
    },
    title: {
      margin: "18px 0 12px",
      fontFamily: '"Fraunces", "Pretendard Variable", "Pretendard", serif',
      fontVariationSettings: '"opsz" 96, "SOFT" 100',
      fontSize: "clamp(2.8rem, 6vw, 5.75rem)",
      lineHeight: 0.96,
      letterSpacing: "-0.035em",
      color: isDark ? "#fff" : "var(--ink)",
    },
    accentTitle: {
      color: colors.primary,
    },
    subtitle: {
      maxWidth: 760,
      fontSize: 18,
      lineHeight: 1.75,
      color: isDark ? "rgba(248,245,238,0.78)" : "var(--ink-2)",
    },
    section: {
      marginTop: 28,
      padding: "24px 0 0",
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
    },
    sectionCard: {
      marginTop: 16,
      padding: "22px 22px",
      borderRadius: 22,
      background: isDark ? "rgba(255,255,255,0.05)" : "#fff",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
      boxShadow: isDark
        ? "0 18px 40px rgba(0,0,0,0.22)"
        : "0 14px 30px -20px rgba(28,24,56,0.16)",
    },
    h2: {
      margin: 0,
      fontFamily: '"Fraunces", "Pretendard Variable", "Pretendard", serif',
      fontVariationSettings: '"opsz" 36, "SOFT" 100',
      fontSize: 32,
      letterSpacing: "-0.03em",
      color: isDark ? "#fff" : "var(--ink)",
    },
    h3: {
      margin: "0 0 8px",
      fontSize: 17,
      fontWeight: 700,
      color: isDark ? "#F7EED3" : "var(--ink)",
    },
    body: {
      fontSize: 16,
      lineHeight: 1.8,
      color: isDark ? "rgba(248,245,238,0.8)" : "var(--ink-2)",
    },
    list: {
      margin: "10px 0 0",
      paddingLeft: 20,
      lineHeight: 1.8,
      color: isDark ? "rgba(248,245,238,0.8)" : "var(--ink-2)",
      fontSize: 15,
    },
    badgeRow: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 14,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "5px 10px",
      borderRadius: 999,
      background: isDark ? "rgba(255,255,255,0.08)" : colors.soft,
      color: isDark ? "#F3D27A" : colors.primary,
      fontSize: 12,
      fontWeight: 700,
    },
    link: {
      color: colors.primary,
      fontWeight: 600,
      textDecoration: "none",
    },
    tableWrap: {
      overflowX: "auto",
      marginTop: 14,
      borderRadius: 18,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 14,
      background: isDark ? "rgba(255,255,255,0.03)" : "#fff",
    } as React.CSSProperties,
    thtd: {
      padding: 12,
      textAlign: "left",
      verticalAlign: "top",
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
      color: isDark ? "rgba(248,245,238,0.84)" : "var(--ink-2)",
    },
    langSection: {
      marginTop: 44,
      paddingTop: 24,
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "var(--rule-soft)"}`,
    },
  };
};
